import { AlertCircle, Check, Lock, Mail } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Card } from '../components/ui/card'
import { FormField } from '../components/ui/form-field'
import { Input } from '../components/ui/input'
import { LoadingSpinner } from '../components/ui/loading-spinner'
import { PageHeader } from '../components/ui/page-header'
import { UnifiedButton } from '../components/ui/unified-button'
import { supabase } from '../lib/supabase'

const ResetPasswordPage = () => {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [isRequestingReset, setIsRequestingReset] = useState(false)
  const [resetEmailSent, setResetEmailSent] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showPasswordForm, setShowPasswordForm] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false)
  const [isCheckingSession, setIsCheckingSession] = useState(true)

  // Check for existing session on page load
  useEffect(() => {
    const checkSession = async () => {
      try {
        const {
          data: { session }
        } = await supabase.auth.getSession()

        if (session && session.user) {
          // User has a valid session (came from reset link), show password form
          setShowPasswordForm(true)
        } else {
          // No session, show email form
          setShowPasswordForm(false)
        }
      } catch (err) {
        console.error('Error checking session:', err)
        setError('Failed to verify reset session')
      } finally {
        setIsCheckingSession(false)
      }
    }

    checkSession()
  }, [])

  const handleRequestReset = async () => {
    if (!email) {
      setError('Please enter your email address')
      return
    }

    setError(null)
    setIsRequestingReset(true)

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      })

      if (error) throw error

      setResetEmailSent(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send reset email')
    } finally {
      setIsRequestingReset(false)
    }
  }

  const handlePasswordUpdate = async () => {
    setError(null)

    // Validate passwords
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setIsUpdatingPassword(true)

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      })

      if (error) throw error

      toast.success('Password updated successfully!')
      navigate('/login')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update password')
    } finally {
      setIsUpdatingPassword(false)
    }
  }

  // Show loading while checking session
  if (isCheckingSession) {
    return (
      <div className="mx-auto max-w-md p-4 md:p-6">
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="md" />
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto w-full max-w-xl space-y-6 p-4 md:p-6">
      <PageHeader
        title="Reset Password"
        description={
          showPasswordForm ? 'Enter your new password' : 'Enter your email to reset your password'
        }
        icon={Lock}
      />

      <Card className="max-w-none min-w-0 p-6">
        <div className="space-y-4">
          {error && (
            <div className="text-destructive bg-destructive/10 flex items-start gap-2 rounded-md p-3 text-sm">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {resetEmailSent && (
            <div className="flex items-start gap-2 rounded-md bg-green-50 p-3 text-sm text-green-600 dark:bg-green-900/20 dark:text-green-400">
              <Check className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <div>
                <div className="font-medium">Reset email sent!</div>
                <div className="mt-1 text-sm opacity-80">
                  Check your email for a link to reset your password.
                </div>
              </div>
            </div>
          )}

          {showPasswordForm && !error && (
            <div className="flex items-start gap-2 rounded-md bg-blue-50 p-3 text-sm text-blue-600 dark:bg-blue-900/20 dark:text-blue-400">
              <Lock className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <div>
                <div className="font-medium">Reset link verified!</div>
                <div className="mt-1 text-sm opacity-80">Enter your new password below.</div>
              </div>
            </div>
          )}

          {!showPasswordForm ? (
            // Email form
            <>
              <FormField label="Email Address">
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  disabled={isRequestingReset || resetEmailSent}
                />
              </FormField>

              <div className="flex gap-2 pt-2">
                <UnifiedButton
                  onClick={handleRequestReset}
                  disabled={isRequestingReset || resetEmailSent || !email}
                  className="flex-1"
                >
                  {isRequestingReset ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Sending...
                    </>
                  ) : resetEmailSent ? (
                    <>
                      <Mail className="mr-2 h-4 w-4" />
                      Email Sent
                    </>
                  ) : (
                    'Send Reset Email'
                  )}
                </UnifiedButton>
                <UnifiedButton
                  variant="outline"
                  onClick={() => navigate('/login')}
                  disabled={isRequestingReset}
                >
                  Back to Login
                </UnifiedButton>
              </div>
            </>
          ) : (
            // Password form
            <>
              <FormField label="New Password">
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  minLength={8}
                  disabled={isUpdatingPassword}
                />
              </FormField>

              <FormField label="Confirm New Password">
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  minLength={8}
                  disabled={isUpdatingPassword}
                />
              </FormField>

              <div className="flex gap-2 pt-2">
                <UnifiedButton
                  onClick={handlePasswordUpdate}
                  disabled={isUpdatingPassword || !newPassword || !confirmPassword}
                  className="flex-1"
                >
                  {isUpdatingPassword ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Updating...
                    </>
                  ) : (
                    'Update Password'
                  )}
                </UnifiedButton>
                <UnifiedButton
                  variant="outline"
                  onClick={() => navigate('/login')}
                  disabled={isUpdatingPassword}
                >
                  Cancel
                </UnifiedButton>
              </div>
            </>
          )}
        </div>
      </Card>
    </div>
  )
}

export default ResetPasswordPage
